import { makeAutoObservable } from "mobx";
import { supabaseSelect, wrap } from "../util/supabase-wrapper";
import { Child } from "./child";
import { globalStores } from "./globalStores";
import { supabase, definitions } from "./supabase/client";

type TimeSlot = {
  id: number;
  day: number;
  startTime: number;
};

//TODO: Consider about renaming this because class is a reseved keyword in js
//TODO: Combine all the auto observable classes to inherit shared stuff from a single class
export class Class {
  class: Partial<
    Omit<definitions["Class"], "startDate" | "endDate"> & {
      startDate: Date;
      endDate: Date;
    }
  > = {};
  childAccounts: Partial<definitions["ChildAccount"]>[] = [];
  teacherAccounts: Partial<definitions["TeacherAccount"]>[] = [];
  childs: Child[];
  timeSlots: Partial<definitions["TimeSlot"]>[] = [];
  level: Partial<definitions["Level"]> = {};

  constructor() {
    makeAutoObservable(this);
  }

  static async getAllClasses(teacherId?: number) {
    /**
     * Returns classes specific to a teacher if teacherId is defined, else return all classes.
     */

    const classIdObjs = wrap(
      await supabase
        .from("Class")
        .select("id,teacherAccount:ClassTeacher(TeacherAccount(id))")
    );

    // console.log({ classIdObjs });

    const relevantClassIdObjs = classIdObjs.filter((classIdObj) => {
      //The function filters out all the classes that do not belong to the specified teacher, if teacherId is provided. Otherwise, all classes are returned.
      if (
        classIdObj.teacherAccount?.some(
          (teacherAccount) => teacherAccount?.TeacherAccount?.id === teacherId
        ) ||
        !teacherId
      ) {
        return true;
      }

      return false;
    });

    const allClasses = await Promise.all(
      relevantClassIdObjs.map((classIdObj) => {
        const newClass = new Class();
        return newClass.init(classIdObj.id);
      })
    );

    // console.log({ allClasses });

    return allClasses;
  }

  async init(id: number) {
    this.class.id = id;
    await this.pullClassDetails();

    return this;
  }

  //TODO: Make this require authentication
  async addStudent(childId: number) {
    //TODO: Add test case for this
    if (
      this.childAccounts.some((childAccount) => childAccount.id === childId)
    ) {
      throw new Error("That child is already in the class!");
    }

    await supabase
      .from("ChildClass")
      .insert({
        child: childId,
        class: this.class.id,
      })
      .throwOnError();

    await this.pullClassDetails();
    return this;
  }

  //TODO: Make this require authentication
  async removeStudent(childId: number) {
    await supabase
      .from("ChildClass")
      .delete()
      .match({
        child: childId,
        class: this.class.id,
      })
      .throwOnError();

    await this.pullClassDetails();
    return this;
  }

  //TODO: Make this require authentication
  async addTeacher(teacherId: number) {
    if (this.class.id === undefined) {
      throw new Error("Missing class id");
    }

    await supabase
      .from("ClassTeacher")
      .insert({
        teacher: teacherId,
        class: this.class.id,
      })
      .throwOnError();

    await this.pullClassDetails();
    await globalStores.teacherStore.pullUserDetails();
    return this;
  }

  //TODO: Make this require authentication
  async removeTeacher(teacherId: number) {
    await supabase
      .from("ClassTeacher")
      .delete()
      .match({
        teacher: teacherId,
        class: this.class.id,
      })
      .throwOnError();

    await this.pullClassDetails();
    await globalStores.teacherStore.pullUserDetails();
    return this;
  }

  /** Timeslots will go by integer for start time in 24H format, GMT +8 */
  async addTimeSlot(cronMinHrDay: string) {
    if (!this.class.id) {
      throw new Error("Missing class id!");
    }

    const curTimeSlots = wrap(
      await supabase
        .from("TimeSlot")
        .select()
        .match({ cronMinHrDay, class: this.class.id })
    );

    if (curTimeSlots.length > 0) {
      throw new Error("Time slot already exists!");
    }

    await supabase
      .from("TimeSlot")
      .insert({ cronMinHrDay, class: this.class.id })
      .throwOnError();

    await this.pullClassDetails();
    return this;
  }

  async updateTimeSlotById(timeSlotId: number, cronMinHrDay: string) {
    await supabase
      .from("TimeSlot")
      .update({ cronMinHrDay })
      .match({
        id: timeSlotId,
      })
      .throwOnError();

    await this.pullClassDetails();
    return this;
  }

  private async pullClassDetails() {
    //TODO: Split into base details and full details, otherwise may get laggy
    if (!this.class.id) {
      throw new Error(
        "ID of class must be populated with init before running!"
      );
    }

    const allDetails = wrap(
      await supabaseSelect(
        "Class",
        "*,ChildClass(*,ChildAccount(*)),ClassTeacher(*,TeacherAccount(*)),TimeSlot(*),Level(*)",
        this.class.id
      )
    );

    // const allDetails = wrap(
    //   await supabase
    //     .from("Class")
    //     .select(
    //       "*,ChildClass(*,ChildAccount(*)),ClassTeacher(*,TeacherAccount(*)),TimeSlot(*),Level(*)"
    //     )
    //     .match({ id: this.class.id })
    // );

    //TODO: Consider giving the table aliases for ease of conversion later on
    //TODO: Consider making the child accounts, teacher accounts, here into the proper objects
    this.childAccounts = allDetails[0].ChildClass.map(
      (childClass: any) => childClass.ChildAccount
    );
    this.teacherAccounts = allDetails[0].ClassTeacher.map(
      (classTeacher: any) => classTeacher.TeacherAccount
    );
    this.timeSlots = allDetails[0].TimeSlot;
    this.level = { ...this.level, ...allDetails[0].Level };

    const { ChildClass, ClassTeacher, TimeSlot, Level, ...newClass } =
      allDetails[0];

    //@ts-ignore
    if (!newClass.startDate || !newClass.endDate) {
      throw new Error("Start date or end date is missing!");
    }

    this.class = {
      ...newClass,
      //@ts-ignore
      startDate: new Date(newClass.startDate),
      //@ts-ignore
      endDate: new Date(newClass.endDate),
    };

    this.setChilds(
      await Promise.all(
        this.childAccounts.map(async (childAccount) => {
          const newChild = new Child();
          await newChild.init(childAccount.id!);
          return newChild;
        })
      )
    );

    return this;
  }

  private setChilds(newChilds: typeof this.childs) {
    this.childs = newChilds;
  }
}

export default new Class();
